import { getProjectAndSummaryReports, getProjectAndSummaryReportsList } from '../../../../../api/projectApi';
import { generatePdfOnePagerProjectReport, generatePdfOnePagerProjectReportBlob } from '../Generators/OnePagerProjectReportGenerator';

export interface OnePagerBatchSuccess {
  projectId: number;
  projectCode?: string;
  filename: string;
  blob: Blob;
}

export interface OnePagerBatchFailure {
  projectId: number;
  projectCode?: string;
  reason?: string;
}

export interface OnePagerBatchProgress {
  total: number;
  processed: number;
  percent: number;
  currentProjectId?: number;
  successCount: number;
  failureCount: number;
}

export interface OnePagerBatchResult {
  successes: OnePagerBatchSuccess[];
  failures: OnePagerBatchFailure[];
  total: number;
}

export const downloadOnePagerReport = async (currentProject, setIsLoading) => {
  if (!currentProject) return;

  const reportData = await getProjectAndSummaryReports(currentProject);

  if (!reportData) {
    console.error('No data received from getProjectAndSummaryReports, aborting PDF generation.');
    return;
  }

  await generatePdfOnePagerProjectReport({ data: reportData, onLoading: setIsLoading });
};

const resolveProjectIdFromReportData = (reportData: any): number | undefined => {
  const rawId = reportData?.id ?? reportData?.projectId ?? reportData?.projectID ?? reportData?.project?.id;
  const projectId = Number(rawId);
  return Number.isFinite(projectId) ? projectId : undefined;
};

const resolveProjectCodeFromReportData = (reportData: any): string | undefined => {
  const code = reportData?.code ?? reportData?.projectCode ?? reportData?.project?.code ?? reportData?.operationNumber;
  return typeof code === 'string' && code.trim() ? code : undefined;
};

export const generateOnePagerReportsBatch = async (
  projectIds: number[],
  options?: {
    setIsLoading?: (loading: boolean) => void;
    onProgress?: (progress: OnePagerBatchProgress) => void;
  },
): Promise<OnePagerBatchResult> => {
  const { setIsLoading, onProgress } = options || {};
  const total = projectIds?.length || 0;
  const successes: OnePagerBatchSuccess[] = [];
  const failures: OnePagerBatchFailure[] = [];

  if (!total) {
    return { successes, failures, total: 0 };
  }

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('offline');
  }

  setIsLoading?.(true);
  onProgress?.({
    total,
    processed: 0,
    percent: 0,
    successCount: 0,
    failureCount: 0,
  });

  try {
    const reportDataList = await getProjectAndSummaryReportsList(projectIds);

    if (!Array.isArray(reportDataList) || reportDataList.length === 0) {
      return {
        successes: [],
        failures: projectIds.map(projectId => ({ projectId, reason: 'no_report_data' })),
        total,
      };
    }

    const reportDataByProjectId = new Map<number, any>();
    reportDataList.forEach((reportData, index) => {
      const projectId = resolveProjectIdFromReportData(reportData);
      if (projectId !== undefined && !reportDataByProjectId.has(projectId)) {
        reportDataByProjectId.set(projectId, reportData);
      }
      if (projectId === undefined && projectIds[index] !== undefined && !reportDataByProjectId.has(projectIds[index])) {
        reportDataByProjectId.set(projectIds[index], reportData);
      }
    });

    let processed = 0;
    for (const projectId of projectIds) {
      const reportData = reportDataByProjectId.get(projectId);
      const projectCode = resolveProjectCodeFromReportData(reportData);

      if (!reportData) {
        failures.push({ projectId, reason: 'project_data_not_found' });
      }
      else {
        try {
          const generated = await generatePdfOnePagerProjectReportBlob({ data: reportData, onLoading: () => null });
          if (!generated?.blob || !generated?.filename) {
            failures.push({ projectId, projectCode, reason: 'invalid_pdf_output' });
          } else {
            successes.push({
              projectId,
              projectCode,
              filename: generated.filename,
              blob: generated.blob,
            });
          }
        } catch (error: any) {
          failures.push({ projectId, projectCode, reason: error?.message || 'pdf_generation_failed' });
        }
      }

      processed += 1;
      onProgress?.({
        total,
        processed,
        percent: Math.round((processed / total) * 100),
        currentProjectId: projectId,
        successCount: successes.length,
        failureCount: failures.length,
      });
    }

    return { successes, failures, total };
  } finally {
    setIsLoading?.(false);
  }
};

export const downloadOnePagerReportsList = async (projectIds: number[], setIsLoading?: (loading: boolean) => void) => {
  if (!projectIds?.length) return false;
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('offline');
  }

  setIsLoading?.(true);
  try {
    const reportDataList = await getProjectAndSummaryReportsList(projectIds);

    if (!Array.isArray(reportDataList) || reportDataList.length === 0) {
      console.error('No data received from getProjectAndSummaryReportsList, aborting PDF generation.');
      return false;
    }

    let successCount = 0;
    for (const reportData of reportDataList) {
      if (!reportData) continue;
      try {
        await generatePdfOnePagerProjectReportBlob({ data: reportData, onLoading: () => null });
        successCount += 1;
      } catch (error) {
        console.error('Error generating PDF for project report', error);
      }
    }

    return successCount > 0;
  } finally {
    setIsLoading?.(false);
  }
};
